import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { ExperimentsList } from './ExperimentsList';
import { CreateExperimentDialog } from './CreateExperimentDialog';
import { CreateGoalDialog } from './CreateGoalDialog';
import { GoalView } from './GoalView';
import { ExperimentView } from './ExperimentView';
import { Button } from './ui/button';
import { Plus, Code, ExternalLink, Loader2, Target } from 'lucide-react';
import { Experiment, Goal } from '@/types/experiment';

interface ExperimentsProps {
  propelauth: any;
  user: any;
  onNavigateToPage?: (page: string) => void;
}

export function Experiments({ propelauth, user, onNavigateToPage }: ExperimentsProps) {
  const [experiments, setExperiments] = useState<Experiment[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateGoalDialogOpen, setIsCreateGoalDialogOpen] = useState(false);
  const [showPythonBanner, setShowPythonBanner] = useState(true);
  
  // Router hooks
  const { experimentId, goalId } = useParams();
  const navigate = useNavigate();
  
  // Find selected experiment and goal from URL params
  const selectedExperiment = experimentId ? experiments.find(exp => exp.id === experimentId) : null;
  const selectedGoal = goalId ? goals.find(goal => goal.id === goalId) : null;

  // Get user ID from PropelAuth
  const getUserId = async () => {
    try {
      const authInfo = await propelauth.getAuthenticationInfoOrNull();
      return authInfo?.user?.userId || null;
    } catch (error) {
      console.error('Error getting user ID:', error);
      return null;
    }
  };

  // Fetch goals from the database
  const fetchGoals = async () => {
    try {
      setIsLoading(true);
      
      // Get user ID properly
      const userId = await getUserId();
      if (!userId) {
        console.error('No user ID available');
        setGoals([]);
        return;
      }
      
      // Get auth headers
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      const response = await fetch('/api/goals', {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        // Convert API response to match Goal interface
        const goalsWithDates = (data.goals || []).map((goal: any) => ({
          ...goal,
          createdAt: new Date(goal.created_at)
        }));
        setGoals(goalsWithDates);
      } else {
        console.error('Failed to fetch goals:', response.statusText);
        // If no goals exist, we'll show an empty state
        setGoals([]);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
      // If there's an error, we'll show an empty state
      setGoals([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch experiments from the database
  const fetchExperiments = async () => {
    try {
      const userId = await getUserId();
      if (!userId) {
        console.error('No user ID available for experiments');
        setExperiments([]);
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      const response = await fetch('/api/experiments', {
        method: 'GET',
        headers,
      });

      if (response.ok) {
        const data = await response.json();
        // Convert API response to match Experiment interface
        const experimentsWithDates = (data.experiments || []).map((exp: any) => ({
          id: exp.external_id,
          name: exp.experiment_data?.name || 'Untitled Experiment',
          goalId: exp.goal_id,
          hypothesis: exp.experiment_data?.hypothesis || '',
          parameters: exp.experiment_data?.parameters || {},
          results: exp.al_experiment_results?.length > 0 ? exp.al_experiment_results[exp.al_experiment_results.length - 1]?.result_data : undefined,
          status: exp.status === 'active' ? 'running' : exp.status === 'completed' ? 'completed' : 'failed',
          createdAt: new Date(exp.created_at),
          completedAt: exp.updated_at ? new Date(exp.updated_at) : undefined,
        }));
        setExperiments(experimentsWithDates);
      } else {
        console.error('Failed to fetch experiments:', response.statusText);
        setExperiments([]);
      }
    } catch (error) {
      console.error('Error fetching experiments:', error);
      setExperiments([]);
    }
  };

  useEffect(() => {
    fetchGoals();
    fetchExperiments();
  }, []);

  // Show loading state when we're trying to load a specific experiment or goal
  if ((experimentId || goalId) && isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {experimentId ? 'Loading experiment...' : 'Loading goal...'}
          </p>
        </div>
      </div>
    );
  }

  // IMPORTANT: If we have an experimentId/goalId but haven't found it yet, keep loading
  // This prevents the flash of the main dashboard while data is being processed
  if ((experimentId || goalId) && !isLoading) {
    // If we have experimentId but no experiment found and we have data loaded
    if (experimentId && !selectedExperiment && experiments.length > 0) {
      // Experiment not found in loaded data, redirect
      navigate('/experiments');
      return null;
    }
    
    // If we have goalId but no goal found and we have data loaded  
    if (goalId && !selectedGoal && goals.length > 0) {
      // Goal not found in loaded data, redirect
      navigate('/experiments');
      return null;
    }
    
    // If we still don't have the specific item and no data loaded, show not found
    if ((experimentId && !selectedExperiment && experiments.length === 0) ||
        (goalId && !selectedGoal && goals.length === 0)) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <p className="text-muted-foreground">
              {experimentId ? 'Experiment not found' : 'Goal not found'}
            </p>
            <Button onClick={() => navigate('/experiments')} className="mt-4">
              Back to Experiments
            </Button>
          </div>
        </div>
      );
    }
    
    // If we have experimentId but haven't found it yet (data still processing)
    if (experimentId && !selectedExperiment) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading experiment...</p>
          </div>
        </div>
      );
    }
    
    // If we have goalId but haven't found it yet (data still processing)
    if (goalId && !selectedGoal) {
      return (
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
            <p className="text-muted-foreground">Loading goal...</p>
          </div>
        </div>
      );
    }
  }

  const handleCreateExperiment = async (experimentData: { name: string; goalId?: string; hypothesis: string; parameters: Record<string, any>; }) => {
    try {
      const userId = await getUserId();
      if (!userId) {
        console.error('No user ID available');
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        'x-user-id': userId,
      };

      // Transform frontend data to API format
      const apiPayload = {
        experiment_data: {
          name: experimentData.name,
          hypothesis: experimentData.hypothesis,
          parameters: experimentData.parameters
        },
        goal_id: experimentData.goalId || null,
        status: 'active'
      };

      const response = await fetch('/api/experiments', {
        method: 'POST',
        headers,
        body: JSON.stringify(apiPayload),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Create experiment object for UI
        const newExperiment: Experiment = {
          id: data.experiment.external_id,
          name: experimentData.name,
          goalId: experimentData.goalId,
          hypothesis: experimentData.hypothesis,
          parameters: experimentData.parameters,
          status: 'running',
          createdAt: new Date(data.experiment.created_at || new Date()),
        };

        setExperiments(prev => [...prev, newExperiment]);
        setIsCreateDialogOpen(false);
      } else {
        const errorData = await response.json();
        console.error('Failed to create experiment:', errorData);
        // TODO: Show error message to user
      }
    } catch (error) {
      console.error('Error creating experiment:', error);
      // TODO: Show error message to user
    }
  };

  const handleCreateGoal = (newGoal: Goal) => {
    // Add the new goal to the list
    setGoals(prev => [newGoal, ...prev]);
    setIsCreateGoalDialogOpen(false);
  };

  const handleUpdateResults = (experimentId: string, results: Record<string, any>) => {
    setExperiments(prev => 
      prev.map(exp => 
        exp.id === experimentId 
          ? { ...exp, results, status: 'completed' as const, completedAt: new Date() }
          : exp
      )
    );
  };

  const handleGoalClick = (goal: Goal) => {
    navigate(`/goals/${goal.id}`);
  };

  const handleBackToGoals = () => {
    navigate('/experiments');
  };

  const handleGoalUpdated = (updatedGoal: Goal) => {
    // Update the goal in the goals list
    setGoals(prev => prev.map(goal => 
      goal.id === updatedGoal.id ? updatedGoal : goal
    ));
    // Stay on the same goal page
    navigate(`/goals/${updatedGoal.id}`);
  };

  const handleExperimentClick = (experiment: Experiment) => {
    navigate(`/experiments/${experiment.id}`);
  };

  const handleBackToExperiments = () => {
    navigate('/experiments');
  };

  // If an experiment is selected via URL, show the experiment view
  if (experimentId && selectedExperiment) {
    return (
      <ExperimentView 
        experiment={selectedExperiment} 
        goals={goals}
        onBack={handleBackToExperiments} 
        onNavigateToDocumentation={() => onNavigateToPage?.('documentation')}
        onExperimentUpdated={(updatedExp) => {
          // Update the experiment in the local state
          setExperiments(prev => 
            prev.map(exp => 
              exp.id === updatedExp.id ? updatedExp : exp
            )
          );
          // Stay on the same experiment page
          navigate(`/experiments/${updatedExp.id}`);
          // Refetch goals to update experiment counts
          fetchGoals();
        }}
        getUserId={getUserId}
      />
    );
  }

  // If a goal is selected via URL, show the goal view
  if (goalId && selectedGoal) {
    return (
      <GoalView 
        goal={selectedGoal} 
        onBack={handleBackToGoals} 
        onGoalUpdated={handleGoalUpdated}
        propelauth={propelauth}
      />
    );
  }

  const statsData = [
    { title: 'Total Goals', value: goals.length },
    { title: 'Active Experiments', value: experiments.filter(e => e.status === 'running').length },
    { title: 'Completed', value: experiments.filter(e => e.status === 'completed').length },
    { title: 'Unassigned', value: experiments.filter(e => !e.goalId).length, highlight: experiments.filter(e => !e.goalId).length > 0 },
  ];

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-3xl font-bold">Experiments</h1>
        <p className="text-muted-foreground">Manage your experiments and track their progress</p>
      </div>

      {/* Python Client Integration Banner */}
      {showPythonBanner && (
        <Card className="bg-gradient-to-r from-blue-600 to-purple-600 text-white border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Code className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold">🐍 Track Experiments Programmatically</h3>
                  <p className="text-blue-100 text-sm">
                    Use our Python client to integrate experiment tracking directly into your systems
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  size="sm" 
                  className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                  onClick={() => window.open('/docs/python-client.html', '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  View Docs
                </Button>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  className="text-white hover:bg-white/20"
                  onClick={() => setShowPythonBanner(false)}
                >
                  ×
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {statsData.map((stat) => (
          <Card key={stat.title} className={stat.highlight ? 'border-amber-400 bg-amber-50' : ''}>
            <CardContent className="p-6">
              <div className={`text-2xl font-bold ${stat.highlight ? 'text-amber-600' : ''}`}>
                {stat.value}
                {stat.highlight && stat.value > 0 && <span className="ml-1">⚠️</span>}
              </div>
              <p className={`text-xs ${stat.highlight ? 'text-amber-700' : 'text-muted-foreground'}`}>
                {stat.title}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Goals Section */}
        <Card className="bg-card">
          <CardHeader className="bg-gradient-to-r from-pink-500 to-red-500 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                📊 Goals
              </span>
              <Button 
                size="sm" 
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                onClick={() => setIsCreateGoalDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create Goal
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin mr-2" />
                <span className="text-muted-foreground">Loading goals...</span>
              </div>
            ) : goals.length === 0 ? (
              <div className="text-center py-8 bg-background rounded-lg border">
                <Target className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                <h4 className="text-lg font-medium mb-2">No goals yet</h4>
                <p className="text-muted-foreground mb-4">
                  Create your first goal to start organizing your experiments
                </p>
                <Button 
                  onClick={() => setIsCreateGoalDialogOpen(true)}
                  className="bg-primary hover:bg-primary/90"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Goal
                </Button>
                <div className="mt-4 text-sm text-muted-foreground">
                  💡 <em>Tip: Goals help you track progress across related experiments!</em>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {goals.map((goal) => (
                  <div 
                    key={goal.id} 
                    className="flex items-center justify-between p-3 bg-background rounded-lg border hover:bg-accent/50 cursor-pointer transition-colors"
                    onClick={() => handleGoalClick(goal)}
                  >
                    <div>
                      <h4 className="font-medium">{goal.name}</h4>
                      {goal.description && goal.description !== goal.name && (
                        <p className="text-sm text-muted-foreground">{goal.description}</p>
                      )}
                      {goal.success_metric && (
                        <div className="text-sm text-muted-foreground mt-1">
                          📈 Success Metric: {goal.success_metric}
                          {goal.target_value && ` (Target: ${goal.target_value})`}
                        </div>
                      )}
                    </div>
                    <div className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-sm font-semibold">
                      {(goal as any).experiment_count || 0}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Experiments Section */}
        <Card className="bg-card">
          <CardHeader className="bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-t-lg">
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                🧪 Experiments
              </span>
              <Button 
                size="sm" 
                className="bg-white/20 hover:bg-white/30 text-white border-white/30"
                onClick={() => setIsCreateDialogOpen(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Create
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {experiments.length === 0 ? (
              <div className="text-center py-8 bg-background rounded-lg border">
                <h4 className="text-lg font-medium mb-2">No experiments yet</h4>
                <p className="text-muted-foreground">
                  Create your first experiment to start tracking results
                </p>
                <div className="mt-4 text-sm text-muted-foreground">
                  💡 <em>Tip: You can create experiments with or without goals!</em>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Show unassigned experiments section if any exist */}
                {experiments.filter(e => !e.goalId).length > 0 && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-amber-600">⚠️</span>
                      <h4 className="font-medium text-amber-800">
                        Unassigned Experiments ({experiments.filter(e => !e.goalId).length})
                      </h4>
                    </div>
                    <p className="text-sm text-amber-700 mb-3">
                      These experiments don't have goals assigned yet. Consider organizing them!
                    </p>
                    <div className="space-y-2">
                      {experiments.filter(e => !e.goalId).slice(0, 3).map((exp) => (
                        <div key={exp.id} className="text-sm text-amber-800 bg-amber-100 px-2 py-1 rounded">
                          📊 {exp.name}
                        </div>
                      ))}
                      {experiments.filter(e => !e.goalId).length > 3 && (
                        <div className="text-sm text-amber-600">
                          ... and {experiments.filter(e => !e.goalId).length - 3} more
                        </div>
                      )}
                    </div>
                  </div>
                )}
                
                <ExperimentsList 
                  experiments={experiments} 
                  goals={goals}
                  onUpdateResults={handleUpdateResults}
                  onExperimentClick={handleExperimentClick}
                />
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <CreateExperimentDialog
        open={isCreateDialogOpen}
        onOpenChange={setIsCreateDialogOpen}
        goals={goals}
        onCreateExperiment={handleCreateExperiment}
      />

      <CreateGoalDialog
        open={isCreateGoalDialogOpen}
        onOpenChange={setIsCreateGoalDialogOpen}
        onCreateGoal={handleCreateGoal}
        propelauth={propelauth}
      />
    </div>
  );
} 